import type { ReactNode, RefObject } from 'react';
import { createRef, Component } from 'react';

import BpkPopover from '@skyscanner/backpack-web/bpk-component-popover';
import BpkText from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { priceChangeThreshold, priceChangeDiff } from './price-change-util';

import type { I18nService } from '@shared/types/i18n';
import type { Stay, Nullable } from '@shared/types/utils';

import STYLES from './PriceChangeReminder.module.scss';

import { withI18n } from '@shared/ui/contexts/i18n';
import {
  getCurrentIntParam,
  getCurrentQueryParam,
} from '@shared/utils-frontend/url/url';

type Props = {
  i18n: I18nService;
  children: ReactNode;
  price: number;
  stay: Stay;
  priceIndex: number;
};

type State = {
  isShow: boolean;
  priceChange: number;
  priceIsIncrease: boolean;
};

const defaultProps = { price: 0, priceIndex: 0 };

const cls = cssModules(STYLES);

const REMINDER_TIME = 5000;
class PriceChangeReminder extends Component<Props, State> {
  dayviewPrice: Nullable<number>;

  priceType: Nullable<string>;

  priceDiff: number;

  timeout: Nullable<ReturnType<typeof setTimeout>>;

  target: RefObject<HTMLDivElement>;

  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);

    this.dayviewPrice = getCurrentIntParam('clicked_details_price');
    this.priceType = getCurrentQueryParam('priceType');

    const { currency } = this.props.i18n.culture;

    this.priceDiff = priceChangeDiff(
      this.dayviewPrice,
      this.props.price,
      this.props.stay,
      this.priceType,
    );

    this.state = {
      isShow: Math.abs(this.priceDiff) >= priceChangeThreshold(currency),
      priceChange: Math.abs(this.priceDiff),
      priceIsIncrease: this.priceDiff < 0,
    };
    this.timeout = null;
    this.target = createRef();
  }

  componentDidMount() {
    this.timeout = setTimeout(() => {
      this.setState({ isShow: false });
    }, REMINDER_TIME);
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  getReminderMessage = (i18n: I18nService, priceChange: number) => {
    const priceFormat = i18n.formatCurrency(priceChange);
    if (this.state.priceIsIncrease) {
      return i18n.translate('PriceChange_price_increase', {
        price: priceFormat,
      });
    }
    return i18n.translate('PriceChange_price_decrease', {
      icon: '🎉',
      price: priceFormat,
    });
  };

  render() {
    const { children, i18n, priceIndex } = this.props;
    const { isShow, priceChange, priceIsIncrease } = this.state;

    if (!isShow) {
      return children;
    }

    return (
      <div className={cls('PriceChangeReminder')}>
        <BpkPopover
          id="priceChangeReminder"
          target={<div ref={this.target}>{children}</div>}
          isOpen={isShow && priceIndex <= 1}
          closeButtonText={i18n.translate('Modal_close')}
          label=""
          onClose={() => {}}
          padded={false}
          className={cls(
            'PriceChangeReminder__popover',
            !priceIsIncrease && 'PriceChangeReminder__margin__withColor',
          )}
        >
          <BpkText className={cls('PriceChangeReminder__text')}>
            {this.getReminderMessage(i18n, priceChange)}
          </BpkText>
        </BpkPopover>
      </div>
    );
  }
}

export default withI18n(PriceChangeReminder);
